import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { 
  Users, Plus, Calendar, MapPin, Clock, PoundSterling,
  ArrowLeft, Check, X, AlertTriangle, ExternalLink, Baby,
  AlertCircle, ChevronRight, Flag
} from "lucide-react";
import BottomNav from "@/components/BottomNav";

const SitterPoolDetail = () => {
  const { poolId } = useParams();
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [pool, setPool] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmergencyModal, setShowEmergencyModal] = useState(null);
  const [emergencyReason, setEmergencyReason] = useState("");
  const [emergencyChildren, setEmergencyChildren] = useState([]);
  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    fetchData();
  }, [poolId]);

  const fetchData = async () => {
    try {
      const [poolsRes, bookingsRes, profileRes] = await Promise.all([
        axios.get(`${API}/sitter-pools`),
        axios.get(`${API}/sitter-pools/${poolId}/bookings`),
        axios.get(`${API}/profile`)
      ]);
      
      const foundPool = poolsRes.data.pools?.find(p => p.pool_id === poolId);
      setPool(foundPool);
      setBookings(bookingsRes.data.bookings || []);
      setMyProfile(profileRes.data);
    } catch (error) {
      console.error("Error:", error);
      if (error.response?.status === 404 || error.response?.status === 403) {
        toast.error("Pool not found or access denied");
        navigate("/sitter-pools");
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmBooking = async (bookingId) => {
    try {
      const res = await axios.post(`${API}/sitter-pools/bookings/${bookingId}/confirm`);
      toast.success("Booking confirmed!");
      if (res.data.all_confirmed) {
        toast.success("All members confirmed! Booking is ready.");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm");
    }
  };

  const requestEmergencyRefund = async (bookingId) => {
    if (!emergencyReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }

    try {
      await axios.post(`${API}/sitter-pools/bookings/${bookingId}/emergency-refund`, {
        reason: emergencyReason,
        child_names: emergencyChildren
      });
      toast.success("Emergency refund requested");
      setShowEmergencyModal(null);
      setEmergencyReason("");
      setEmergencyChildren([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request refund");
    }
  };

  const leavePool = async () => {
    if (!confirm("Are you sure you want to leave this pool?")) return;
    
    try {
      await axios.post(`${API}/sitter-pools/${poolId}/leave`);
      toast.success("You've left the pool");
      navigate("/sitter-pools");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave pool");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBookingStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-slate-100 text-slate-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  const isWithin48Hours = (dateStr) => {
    const bookingDate = new Date(dateStr);
    const now = new Date();
    const hoursUntil = (bookingDate - now) / (1000 * 60 * 60);
    return hoursUntil > 0 && hoursUntil <= 48;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F5] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!pool) {
    return (
      <div className="min-h-screen bg-[#FFF9F5] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600">Pool not found</p>
          <Button onClick={() => navigate("/sitter-pools")} className="mt-4">
            Back to Pools
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/sitter-pools" className="p-2 -ml-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {pool.name}
            </h1>
            {pool.description && (
              <p className="text-teal-100 text-sm">{pool.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Members Section */}
      <div className="p-4">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Members</h2>
        <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
          {pool.members?.map(member => (
            <div key={member.user_id} className="flex items-center gap-3 p-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-coral-400 flex items-center justify-center text-white font-bold">
                {member.parent_name?.[0] || '?'}
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-800">{member.parent_name}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Baby className="w-3 h-3" />
                  {member.children_count} children
                </div>
              </div>
              {pool.creator_id === member.user_id && (
                <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">
                  Creator
                </span>
              )}
            </div>
          ))}
          
          {/* Pending Invites */}
          {pool.invited?.length > 0 && (
            <>
              <div className="px-3 py-2 bg-slate-50">
                <p className="text-xs font-medium text-slate-500 uppercase">Pending Invites</p>
              </div>
              {pool.invited.map(invite => (
                <div key={invite.user_id} className="flex items-center gap-3 p-3 opacity-60">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold">
                    {invite.parent_name?.[0] || '?'}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-600">{invite.parent_name}</p>
                    <p className="text-xs text-slate-400">Pending...</p>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {!pool.is_creator && (
          <Button
            onClick={leavePool}
            variant="outline"
            className="w-full mt-3 text-red-600 border-red-200 hover:bg-red-50"
          >
            Leave Pool
          </Button>
        )}
      </div>

      {/* Bookings Section */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-800">Bookings</h2>
          <Button
            onClick={() => navigate(`/sitter-pools/${poolId}/book`)}
            className="bg-coral-500 hover:bg-coral-600 text-white rounded-full"
            size="sm"
            data-testid="create-booking-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            New Booking
          </Button>
        </div>

        {bookings.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-slate-100">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">No bookings yet</p>
            <p className="text-slate-400 text-sm mt-1">Create a booking to share childcare</p>
          </div>
        ) : (
          <div className="space-y-3">
            {bookings.map(booking => (
              <div key={booking.booking_id} className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-800">{booking.title}</h3>
                    {booking.activity_type && (
                      <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
                        {booking.activity_type}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getBookingStatusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {formatDate(booking.scheduled_date)}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {booking.location}
                  </div>
                  <div className="flex items-center gap-2">
                    <PoundSterling className="w-4 h-4 text-slate-400" />
                    Your share: <span className="font-semibold text-slate-800">£{booking.my_share?.amount?.toFixed(2)}</span>
                    <span className="text-xs text-slate-400">
                      ({booking.my_share?.children_count} kids)
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  {!booking.my_confirmed && booking.status === 'pending' && (
                    <Button
                      onClick={() => confirmBooking(booking.booking_id)}
                      size="sm"
                      className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
                      data-testid={`confirm-booking-${booking.booking_id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Confirm
                    </Button>
                  )}

                  {booking.my_confirmed && booking.my_share?.status !== 'refund_requested' && isWithin48Hours(booking.scheduled_date) && (
                    <Button
                      onClick={() => {
                        setShowEmergencyModal(booking.booking_id);
                        setEmergencyChildren(myProfile?.children?.map(c => c.name) || []);
                      }}
                      size="sm"
                      variant="outline"
                      className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`emergency-refund-${booking.booking_id}`}
                    >
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      Emergency
                    </Button>
                  )}

                  {booking.my_share?.status === 'refund_requested' && (
                    <span className="flex-1 text-center text-amber-600 text-sm py-2">
                      Refund requested
                    </span>
                  )}

                  <button
                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors"
                    onClick={() => toast.info("Contact your preferred sitter to arrange childcare for this booking")}
                  >
                    Book Preferred Sitter
                  </button>
                </div>

                {/* Attendance Issues Warning */}
                {booking.attendance_issues?.length > 0 && (
                  <div className="mt-3 p-2 bg-red-50 rounded-lg flex items-start gap-2">
                    <Flag className="w-4 h-4 text-red-500 mt-0.5" />
                    <p className="text-xs text-red-700">
                      {booking.attendance_issues.length} attendance issue(s) reported
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Emergency Refund Modal */}
      {showEmergencyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Emergency Refund</h2>
                  <p className="text-sm text-slate-500">Within 48 hours of booking</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Reason for cancellation
                  </label>
                  <textarea
                    value={emergencyReason}
                    onChange={(e) => setEmergencyReason(e.target.value)}
                    placeholder="e.g., Child is unwell"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    rows={3}
                    data-testid="emergency-reason-input"
                  />
                </div>

                <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-800">
                  <strong>Note:</strong> Refunds are subject to your sitter's cancellation policy. 
                  KiddiPlay commission (10%) is non-refundable.
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEmergencyModal(null);
                    setEmergencyReason("");
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => requestEmergencyRefund(showEmergencyModal)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="submit-emergency-refund"
                >
                  Request Refund
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default SitterPoolDetail;
